import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Check, Send } from 'lucide-react'
import { Button } from './ui/button'
import Picker from './Picker'
import { ID, appwriteDatabaseId, appwriteRidesTableId, appwriteTables, getAppwriteUser } from '../appwrite'

const rideSchema = z.object({
  kind: z.enum(['offer', 'request']),
  direction: z.enum(['Entrada', 'Salida']),
  time: z.string().trim().min(1, 'Indica la hora.'),
  route: z.string().trim().min(2, 'Indica la ruta.'),
  seats: z.coerce.number().int().min(1, 'Indica al menos un cupo.').max(8, 'Máximo 8 cupos.'),
  price: z.string().trim().optional(),
  contactPhone: z.string().trim().regex(/^[0-9+() -]{8,20}$/, 'Indica un número de WhatsApp válido.')
})

function displayTime(date) {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function timeOptions() {
  var now = new Date()
  var soon = new Date(now.getTime() + 15 * 60 * 1000)
  return [
    { value: displayTime(now), label: 'Ahora · ' + displayTime(now) },
    { value: displayTime(soon), label: 'En 15 min · ' + displayTime(soon) },
    { value: '06:00 AM', label: '06:00 AM' },
    { value: '06:30 AM', label: '06:30 AM' },
    { value: '07:00 AM', label: '07:00 AM' },
    { value: '07:30 AM', label: '07:30 AM' },
    { value: '08:00 AM', label: '08:00 AM' },
    { value: '12:00 PM', label: '12:00 PM' },
    { value: '05:00 PM', label: '05:00 PM' },
    { value: '06:00 PM', label: '06:00 PM' }
  ]
}

function selectedGroupRoute() {
  var selectedGroup = window.selectedGroup || ''
  var group = (Array.isArray(window.allGroups) ? window.allGroups : []).find(function (item) {
    return item && item.jid === selectedGroup
  }) || {}
  return {
    label: (group.label || group.name || 'Ruta del grupo').trim(),
    value: (group.places || group.label || group.name || '').trim()
  }
}

function savedContactPhone() {
  try {
    return localStorage.getItem('polipool_contact_phone') || ''
  } catch (error) {
    return ''
  }
}

export default function WhatsAppTemplates({ editingRide, onPublished, onAuthRequired }) {
  var [published, setPublished] = useState(false)
  var [submitError, setSubmitError] = useState('')
  var form = useForm({
    resolver: zodResolver(rideSchema),
    defaultValues: { kind: 'offer', direction: 'Entrada', time: '', route: selectedGroupRoute().value, seats: 1, price: '', contactPhone: savedContactPhone() }
  })
  var kind = form.watch('kind')
  var times = timeOptions()
  var groupRoute = selectedGroupRoute()
  var errors = form.formState.errors
  var firstError = errors.time || errors.route || errors.seats || errors.contactPhone

  useEffect(function () {
    if (!editingRide) return
    form.reset({
      kind: editingRide.parsed && editingRide.parsed.isRequest ? 'request' : 'offer',
      direction: editingRide.parsed && editingRide.parsed.direction === 'SALIDA' ? 'Salida' : 'Entrada',
      time: editingRide.parsed && editingRide.parsed.time || '',
      route: editingRide.route || groupRoute.value,
      seats: Number(editingRide.parsed && editingRide.parsed.seats) || 1,
      price: editingRide.parsed && editingRide.parsed.price ? String(editingRide.parsed.price).replace(/^\$/, '') : '',
      contactPhone: editingRide.sender || savedContactPhone()
    })
  }, [editingRide])

  function pick(name) {
    return function (value) { form.setValue(name, value, { shouldValidate: true }) }
  }

  async function submit(values) {
    setSubmitError('')
    try {
      values.route = groupRoute.value
      if (!appwriteDatabaseId || !appwriteRidesTableId) {
        throw new Error('Falta configurar la tabla de viajes en Appwrite.')
      }
      var user = await getAppwriteUser()
      var rowData = {
        groupId: window.selectedGroup || '', kind: values.kind, direction: values.direction,
        time: values.time, route: values.route, seats: values.seats,
        price: values.price || '', contactPhone: values.contactPhone
      }
      var row = editingRide && editingRide.appwriteRowId
        ? await appwriteTables.updateRow({ databaseId: appwriteDatabaseId, tableId: appwriteRidesTableId, rowId: editingRide.appwriteRowId, data: rowData })
        : await appwriteTables.createRow({ databaseId: appwriteDatabaseId, tableId: appwriteRidesTableId, rowId: ID.unique(), data: rowData })
      localStorage.setItem('polipool_contact_phone', values.contactPhone)
      var directRide = {
        _id: 'appwrite-' + row.$id,
        sender: values.contactPhone,
        timestamp: row.$createdAt,
        groupJid: window.selectedGroup || '',
        route: values.route,
        text: (values.kind === 'request' ? '*BUSCO CUPO*' : '*OFREZCO CUPO*') + '\nDirección: ' + values.direction + '\nHora: ' + values.time + '\nRuta: ' + values.route + '\nCupos: ' + values.seats,
        parsed: {
          direction: values.direction === 'Salida' ? 'SALIDA' : 'ENTRADA',
          time: values.time,
          seats: String(values.seats),
          price: values.price ? '$' + values.price : '',
          isRequest: values.kind === 'request'
        },
        appwriteRowId: row.$id,
        appwriteOwnerId: user.$id
      }
      window.allRides = editingRide
        ? (Array.isArray(window.allRides) ? window.allRides : []).map(function (ride) { return ride._id === editingRide._id ? directRide : ride })
        : [directRide].concat(Array.isArray(window.allRides) ? window.allRides : [])
      window.dispatchEvent(new CustomEvent('appwrite-rides-updated', { detail: { rides: [directRide], replaceId: editingRide && editingRide._id } }))
      if (typeof window.filterData === 'function') window.filterData()
      setPublished(true)
      window.setTimeout(function () {
        if (typeof onPublished === 'function') onPublished()
      }, 900)
    } catch (error) {
      setSubmitError(error.message || 'No se pudo publicar el viaje.')
    }
  }

  return <form onSubmit={form.handleSubmit(submit)} className="space-y-3 px-4 py-4">
    <div className="grid grid-cols-2 gap-2">
      <label className="text-[11px] text-slate-600">Tipo
        <div className="mt-1"><Picker compact value={kind} onChange={pick('kind')} placeholder="Elige el tipo" options={[{ value: 'offer', label: 'Ofrezco cupo' }, { value: 'request', label: 'Busco cupo' }]} /></div>
      </label>
      <label className="text-[11px] text-slate-600">Dirección
        <div className="mt-1"><Picker compact value={form.watch('direction')} onChange={pick('direction')} placeholder="Elige dirección" options={[{ value: 'Entrada', label: 'Hacia ESPOL' }, { value: 'Salida', label: 'Desde ESPOL' }]} /></div>
      </label>
      <label className="text-[11px] text-slate-600">Hora
        <div className="mt-1"><Picker compact value={form.watch('time')} onChange={pick('time')} placeholder="Elige una hora" options={times} /></div>
      </label>
      <label className="text-[11px] text-slate-600">{kind === 'offer' ? 'Cupos' : 'Cupos que necesitas'}
        <input {...form.register('seats')} type="number" min="1" max="8" className="mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs" />
      </label>
    </div>
    <div className="text-[11px] text-slate-600">
      <div className="mb-1">Ruta</div>
      <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700">
        {groupRoute.label}{groupRoute.value && groupRoute.value !== groupRoute.label ? <span className="block pt-0.5 text-[10px] font-normal text-slate-500">{groupRoute.value}</span> : null}
      </div>
    </div>
    {kind === 'offer' && <label className="block text-[11px] text-slate-600">Precio (opcional)
      <input {...form.register('price')} inputMode="decimal" placeholder="1.50" className="mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs" />
    </label>}
    <label className="block text-[11px] text-slate-600">Tu WhatsApp
      <input {...form.register('contactPhone')} inputMode="tel" placeholder="099 123 4567" className="mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs" />
    </label>
    {(firstError || submitError) && <p className="text-[11px] text-red-600">{firstError ? firstError.message : submitError}</p>}
    <Button type="submit" size="sm" disabled={form.formState.isSubmitting || published} className="h-9 w-full text-[11px]">
      {published ? <><Check className="size-3.5" />{editingRide ? 'Guardado' : 'Publicado'}</> : <><Send className="size-3.5" />{form.formState.isSubmitting ? 'Guardando...' : (editingRide ? 'Guardar cambios' : 'Publicar viaje')}</>}
    </Button>
  </form>
}
